import { useState } from "react";
import {
  Box,
  Flex,
  Grid,
  Heading,
  Text,
  Card,
  Button,
  Badge,
  Skeleton,
  DropdownMenu,
  IconButton,
} from "@radix-ui/themes";
import { Link, useNavigate } from "@tanstack/react-router";
import { Plus, MoreHorizontal, ExternalLink, Settings, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useBoards, useDeleteBoard } from "@/hooks/use-api";
import { CreateBoardDialog } from "@/components/boards/create-board-dialog";
import type { Board } from "@/lib/api";
import { buildAppDisplayPath } from "@/lib/display-url";

function BoardCard({ board }: { board: Board }) {
  const deleteBoard = useDeleteBoard();
  const navigate = useNavigate();
  const { t } = useTranslation();

  function handleDelete() {
    if (confirm(t("dashboard.delete_confirm_msg", { name: board.name }))) {
      deleteBoard.mutate(board.slug);
    }
  }

  const roleLabel: Record<string, string> = {
    admin: t("dashboard.role_admin"),
    contributor: t("dashboard.role_contributor"),
    recipient: t("dashboard.role_recipient"),
  };

  return (
    <Card
      asChild
      style={{ cursor: "pointer", transition: "box-shadow 0.15s" }}
    >
      <Link to="/boards/$slug" params={{ slug: board.slug }} style={{ textDecoration: "none", color: "inherit" }}>
        <Flex direction="column" gap="3" p="1">
          {/* Header card */}
          <Flex justify="between" align="start">
            <Flex direction="column" gap="1" style={{ minWidth: 0 }}>
              <Text size="3" weight="bold" truncate>
                {board.name}
              </Text>
              <Text size="1" color="gray">
                mem.pm/{board.slug}
              </Text>
            </Flex>
            <DropdownMenu.Root>
              <DropdownMenu.Trigger>
                <IconButton
                  variant="ghost"
                  color="gray"
                  size="1"
                  onClick={(e) => e.preventDefault()}
                >
                  <MoreHorizontal size={16} />
                </IconButton>
              </DropdownMenu.Trigger>
              <DropdownMenu.Content>
                <DropdownMenu.Item
                  onClick={(e) => {
                    e.preventDefault();
                    navigate({ to: "/boards/$slug", params: { slug: board.slug } });
                  }}
                >
                  <Settings size={14} />
                  {t("dashboard.manage")}
                </DropdownMenu.Item>
                <DropdownMenu.Item asChild>
                  <a
                    href={buildAppDisplayPath(board.slug)}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink size={14} />
                    {t("dashboard.display_view")}
                  </a>
                </DropdownMenu.Item>
                <DropdownMenu.Separator />
                <DropdownMenu.Item
                  color="red"
                  onClick={(e) => {
                    e.preventDefault();
                    handleDelete();
                  }}
                >
                  <Trash2 size={14} />
                  {t("common.delete")}
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Root>
          </Flex>

          {/* Description */}
          {board.description && (
            <Text size="2" color="gray" style={{ lineHeight: "1.5" }}>
              {board.description}
            </Text>
          )}

          {/* Footer */}
          <Flex align="center" justify="between" mt="1">
            <Flex gap="2">
              <Badge color="gray" variant="soft" size="1">
                {t(board.member_count > 1 ? "dashboard.members_plural" : "dashboard.members", { count: board.member_count })}
              </Badge>
              {board.public && (
                <Badge color="green" variant="soft" size="1">
                  {t("dashboard.public")}
                </Badge>
              )}
            </Flex>
            <Badge
              color={board.role === "admin" ? "violet" : "gray"}
              variant="soft"
              size="1"
            >
              {roleLabel[board.role] ?? board.role}
            </Badge>
          </Flex>
        </Flex>
      </Link>
    </Card>
  );
}

function BoardCardSkeleton() {
  return (
    <Card>
      <Flex direction="column" gap="3" p="1">
        <Flex justify="between" align="start">
          <Flex direction="column" gap="2" style={{ flexGrow: 1 }}>
            <Skeleton height="20px" width="60%" />
            <Skeleton height="14px" width="40%" />
          </Flex>
        </Flex>
        <Skeleton height="14px" />
        <Skeleton height="14px" width="80%" />
        <Flex gap="2" mt="1">
          <Skeleton height="20px" width="80px" />
        </Flex>
      </Flex>
    </Card>
  );
}

export function DashboardPage() {
  const [showCreate, setShowCreate] = useState(false);
  const { data: boards, isLoading, isError } = useBoards();
  const { t } = useTranslation();

  return (
    <Box>
      {/* Header */}
      <Flex justify="between" align="center" mb="6">
        <Heading size="5">{t("dashboard.title")}</Heading>
        <Button onClick={() => setShowCreate(true)}>
          <Plus size={16} />
          {t("dashboard.create_board")}
        </Button>
      </Flex>

      {/* États */}
      {isError && (
        <Box p="6" style={{ textAlign: "center" }}>
          <Text color="red">{t("dashboard.error_loading")}</Text>
        </Box>
      )}

      {isLoading && (
        <Grid columns={{ initial: "1", sm: "2", lg: "3" }} gap="4">
          {[...Array(3)].map((_, i) => (
            <BoardCardSkeleton key={i} />
          ))}
        </Grid>
      )}

      {!isLoading && !isError && boards?.length === 0 && (
        <Card>
          <Flex direction="column" align="center" gap="3" py="8">
            <Text size="4" weight="medium">
              {t("dashboard.empty")}
            </Text>
            <Text size="2" color="gray" align="center">
              {t("dashboard.empty_cta")}
            </Text>
            <Button mt="2" onClick={() => setShowCreate(true)}>
              <Plus size={16} />
              {t("dashboard.create_first")}
            </Button>
          </Flex>
        </Card>
      )}

      {!isLoading && boards && boards.length > 0 && (
        <Grid columns={{ initial: "1", sm: "2", lg: "3" }} gap="4">
          {boards.map((board) => (
            <BoardCard key={board.id} board={board} />
          ))}
        </Grid>
      )}

      <CreateBoardDialog open={showCreate} onOpenChange={setShowCreate} />
    </Box>
  );
}
